import React, { useState } from "react";
import {
  MenuItem,
  ListItemText,
  ListItemSecondaryAction,
  IconButton,
  Divider,
  Popover,
} from "@mui/material";
import { BankBlueIcon } from "../../assets/Icon/dashboredIcon";
import { useDispatch } from "react-redux";
import {
  reconnectBank,
  updateLinkBankToken,
  userExchangeToken,
} from "../../stores/clients";
import { usePlaidLink } from "react-plaid-link";
import { setToast } from "../../stores/toast/toastslice";

interface ConnectionDropdownProps {
  connectedBanks?: any[];
  disconnectedBanks?: any[];
  onDisconnect?: () => void;
  onReconnect?: (data: any) => void;
  setSelectedBank?: (data: any) => void;
  setLoading?: (data: any) => void;
  isMobile?: boolean;
  anchorEl: HTMLElement | null;
  onClose: () => void;
}

export default function ConnectionDropdown({
  connectedBanks = [],
  disconnectedBanks = [],
  onDisconnect = () => {},
  onReconnect = (data: any) => {},
  setSelectedBank = (data: any) => {},
  setLoading = (data: any) => {},
  isMobile = false,
  anchorEl,
  onClose,
}: ConnectionDropdownProps) {
  const [btnLoading, setBtnLoading] = useState(false);
  const [linkToken, setLinkToken] = useState<any>(null);
  const dispatch = useDispatch();

  const handleDisconnectClick = (item: any) => {
    setSelectedBank(item);
    onDisconnect();
  };

  const handleReconnect = async (bank?: any, fromPopup = false) => {
    if (bank?.needs_reauth || fromPopup) {
      await dispatch<any>(
        updateLinkBankToken({ id: bank?.id || bank?.plaid_item_id })
      ).then((res: any) => {
        setLinkToken(res?.payload?.link_token);
      });
    } else {
      await dispatch<any>(reconnectBank({ bankId: bank?.id }));
      window.location.reload();
      await dispatch<any>(
        setToast({
          message: `Bank connected successfully!`,
          type: "success",
          title: "Success",
          onClose: () => {},
        })
      );
    }
  };

  if (btnLoading) {
    setTimeout(() => {
      setBtnLoading(false);
    }, 2000);
  }

  const config = {
    token: linkToken,
    onSuccess: async (publickToken: string) => {
      setLoading(true);
      await dispatch<any>(
        userExchangeToken({ public_token: publickToken })
      ).unwrap();
      window.location.reload();
    },
    onExit: () => {
      window.location.reload();
    },
  };
  const { open, ready } = usePlaidLink(config);

  React.useEffect(() => {
    if (ready && linkToken) open();
  }, [linkToken, ready]);

  const renderMenuItem = (
    item: any,
    isAuthNeeded: boolean,
    isDisconnected?: boolean
  ) => (
    <MenuItem
      key={item.id}
      disableRipple
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        padding: "9px 20px",
        cursor: "text",
        "&:hover": { backgroundColor: "transparent" },
      }}
    >
      <span
        style={{
          backgroundColor: "#EAF1FA",
          borderRadius: "100px",
          marginRight: "12px",
          width: "40px",
          height: "40px",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          flexShrink: 0,
        }}
      >
        <BankBlueIcon />
      </span>

      <div style={{ flexGrow: 1 }}>
        <ListItemText
          primary={item?.institution_name}
          secondary={item?.subtitle || ""}
          sx={{
            "& .MuiListItemText-primary": { fontWeight: 500 },
            "& .MuiListItemText-secondary": { fontSize: "12px" },
          }}
        />

        {isDisconnected && (
          <div
            style={{
              color: "#D88150",
              fontSize: "12px",
              marginTop: "2px",
              fontWeight: 500,
              backgroundColor: "#D8815029",
              borderRadius: "100px",
              width: "fit-content",
              padding: "2px 10px",
            }}
          >
            Disconnected
          </div>
        )}
        {isAuthNeeded && (
          <div
            style={{
              color: "#F20F00",
              fontSize: "12px",
              marginTop: "2px",
              fontWeight: 500,
              backgroundColor: "#F20F0029",
              borderRadius: "100px",
              width: "fit-content",
              padding: "2px 10px",
            }}
          >
            Unauthorized
          </div>
        )}
      </div>

      <ListItemSecondaryAction>
        {!isDisconnected && !isAuthNeeded ? (
          <IconButton
            edge="end"
            disableRipple
            style={{
              fontSize: "12px",
              fontWeight: "500",
              color: "#2977CC",
              borderRadius: "6px",
              border: "1px solid #EAF1FA",
              padding: "8px 14px",
              marginRight: "8px",
            }}
            onClick={(e) => {
              e.stopPropagation();
              handleDisconnectClick(item);
            }}
          >
            Disconnect
          </IconButton>
        ) : (
          <IconButton
            edge="end"
            disableRipple
            style={{
              fontSize: "12px",
              fontWeight: "500",
              color: btnLoading ? "#ccc" : isAuthNeeded ? "#F20F00" : "#D88150",
              borderRadius: "6px",
              border: btnLoading
                ? "1px solid #ccc"
                : isAuthNeeded
                ? "1px solid #F20F00"
                : "1px solid #D88150",
              padding: "8px 14px",
              marginRight: "8px",
              cursor: btnLoading ? "not-allowed" : "pointer",
            }}
            onClick={async (e: any) => {
              e.preventDefault();
              setSelectedBank(item);
              setBtnLoading(true);
              await handleReconnect(item);
            }}
          >
            {isAuthNeeded ? "Reauthorize" : "Reconnect"}
          </IconButton>
        )}
      </ListItemSecondaryAction>
    </MenuItem>
  );

  const authorizedBanks = connectedBanks.filter((b) => !b.needs_reauth);
  const unauthorizedBanks = connectedBanks.filter((b) => b.needs_reauth);
  const hasConnections =
    authorizedBanks.length > 0 ||
    unauthorizedBanks.length > 0 ||
    disconnectedBanks.length > 0;

  return (
    <Popover
      open={Boolean(anchorEl)}
      anchorEl={anchorEl}
      onClose={onClose}
      anchorOrigin={{
        vertical: "bottom",
        horizontal: "right",
      }}
      transformOrigin={{
        vertical: "top",
        horizontal: "right",
      }}
      PaperProps={{
        sx: {
          width: isMobile ? "90vw" : "400px",
          borderRadius: "8px",
          maxHeight: "70vh",
          overflowY: "auto",
          boxShadow: "0px 2px 8px rgba(0, 0, 0, 0.15)",
          mt: 1,
          padding: "8px 0",
        },
      }}
    >
      {hasConnections ? (
        <>
          {authorizedBanks.map((bank) => renderMenuItem(bank, false))}
          {(unauthorizedBanks.length > 0 || disconnectedBanks.length > 0) &&
            authorizedBanks.length > 0 && (
              <Divider sx={{ my: 1, mx: 3, backgroundColor: "#EAF1FA" }} />
            )}
          {unauthorizedBanks.map((bank) => renderMenuItem(bank, true))}
          {disconnectedBanks.map((bank) => renderMenuItem(bank, false, true))}
        </>
      ) : (
        <MenuItem disabled sx={{ justifyContent: "center" }}>
          No connections found
        </MenuItem>
      )}
    </Popover>
  );
}
